import * as React from 'react';

import {SaveButton} from 'shells/button/save-button/save-button';
import {CalendarSquareRegularCheckIcon} from 'shells/icon/svg/calendar-square-regular-check-icon';
import {LockRegularIcon} from 'shells/icon/svg/lock-regular-icon';
import {StatusBadge} from 'shells/status-badge/status-badge';

import type {ActivityStatus} from 'nutshell-graphql-types';

import {
    LOG_ACTIVITY_BUTTON_TEXT,
    SCHEDULE_FOLLOW_UP_BUTTON_TEXT,
    CREATE_ACTIVITY_BUTTON_TEXT,
} from './consts';
import {ShouldSyncToCalendarCheckbox} from './should-sync-to-calendar-checkbox';

import styles from './footer.module.css';

type Props = {
    status: ActivityStatus | null | undefined;
    handleLogActivity: () => void;
    handleCreateActivity: () => void;
    isCreatingActivity: boolean;
    isSavingActivity: boolean;
    createError: boolean;
    updateError: boolean;
    canUserLogActivity: boolean | null | undefined;
    isFutureActivity: boolean;
    shouldShowForceSync: boolean;
    isFollowUpActivity: boolean;
    logOnCreate: boolean;
    isPrivate: boolean;
    getSaveButtonRef: () => void;
    isBulkCreatingContacts: boolean;
};

export function RightSection(props: Props) {
    const {
        canUserLogActivity,
        isFutureActivity,
        logOnCreate,
        isFollowUpActivity,
        isBulkCreatingContacts,
    } = props;

    const tooltipText = React.useMemo(() => {
        if (!canUserLogActivity) {
            return 'The host of the meeting must log the activity';
        }
        if (isFutureActivity) {
            return 'You cannot log activities for future meetings';
        }
    }, [canUserLogActivity, isFutureActivity]);

    const saveButtonText = React.useMemo(() => {
        if (logOnCreate) {
            return LOG_ACTIVITY_BUTTON_TEXT;
        }
        if (isFollowUpActivity) {
            return SCHEDULE_FOLLOW_UP_BUTTON_TEXT;
        }

        return CREATE_ACTIVITY_BUTTON_TEXT;
    }, [logOnCreate, isFollowUpActivity]);

    return (
        <div className={styles['footer-right-container']}>
            {props.status ? (
                <>
                    {(props.status === 'SCHEDULED' || props.status === 'OVERDUE') && (
                        <>
                            {props.isPrivate &&
                                NutClientConfig.hasActivitySyncContactCreationFF && (
                                    <StatusBadge
                                        level='info--grey'
                                        text='Private'
                                        size='small'
                                        customIcon={LockRegularIcon}
                                        detail='Only you can view this activity until you log it'
                                    />
                                )}
                            <SaveButton
                                key='log-activity'
                                size='big'
                                variant='primary'
                                // @ts-expect-error
                                onClick={props.handleLogActivity}
                                isSaving={props.isSavingActivity}
                                buttonText={LOG_ACTIVITY_BUTTON_TEXT.buttonText}
                                savingText={LOG_ACTIVITY_BUTTON_TEXT.savingText}
                                successText={LOG_ACTIVITY_BUTTON_TEXT.successText}
                                minWidthFitContent={true}
                                errorSaving={Boolean(props.updateError)}
                                disabled={
                                    !canUserLogActivity ||
                                    isFutureActivity ||
                                    isBulkCreatingContacts
                                }
                                disabledTooltip={tooltipText}
                                icon={CalendarSquareRegularCheckIcon}
                                getButtonRef={props.getSaveButtonRef}
                            />
                        </>
                    )}
                    {props.status === 'LOGGED' && (
                        <StatusBadge
                            level='info'
                            text='Logged'
                            customIcon={CalendarSquareRegularCheckIcon}
                        />
                    )}
                </>
            ) : (
                <>
                    {props.shouldShowForceSync && <ShouldSyncToCalendarCheckbox />}
                    <SaveButton
                        key='create-activity'
                        variant={logOnCreate ? 'primary' : 'save'}
                        icon={logOnCreate ? CalendarSquareRegularCheckIcon : undefined}
                        getButtonRef={props.getSaveButtonRef}
                        // @ts-expect-error
                        onClick={props.handleCreateActivity}
                        isSaving={props.isCreatingActivity}
                        buttonText={saveButtonText.buttonText}
                        savingText={saveButtonText.savingText}
                        successText={saveButtonText.successText}
                        errorSaving={Boolean(props.createError)}
                        minWidthFitContent={true}
                    />
                </>
            )}
        </div>
    );
}
